import { useState, useEffect } from "react";
import { useTheme } from "next-themes";
import { cn } from "@/lib/utils";

type LogoVariant = "full" | "square" | "with-text";

interface LogoProps {
  variant?: LogoVariant;
  className?: string;
  alt?: string;
}

const logoFiles: Record<LogoVariant, { light: string; dark: string }> = {
  full: {
    light: "/logo-light.png",
    dark: "/logo-dark.png",
  },
  square: {
    light: "/logo-square-light.png",
    dark: "/logo-square-dark.png",
  },
  "with-text": {
    light: "/logo-with-text-light.png",
    dark: "/logo-with-text-dark.png",
  },
};

export function Logo({ variant = "full", className, alt = "Reqqy Logo" }: LogoProps) {
  const [mounted, setMounted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    setMounted(true);
  }, []);

  // Reset error state when theme changes
  useEffect(() => {
    setHasError(false);
  }, [resolvedTheme]);

  const files = logoFiles[variant];
  const src = mounted ? (resolvedTheme === "dark" ? files.dark : files.light) : files.light;

  // Fallback to text if image fails to load
  if (hasError) {
    return (
      <span className={cn("font-bold text-foreground", className)}>
        Reqqy
      </span>
    );
  }

  // Show placeholder during SSR/hydration to prevent layout shift
  if (!mounted) {
    return (
      <div 
        className={cn("bg-transparent", className)} 
        style={{ 
          height: variant === "square" ? "2rem" : "2rem",
          width: variant === "square" ? "2rem" : "auto",
          minWidth: variant === "full" ? "4rem" : undefined,
        }} 
        aria-hidden="true"
      />
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      width={variant === "square" ? 32 : 128}
      height={variant === "square" ? 32 : 32}
      className={cn("transition-opacity duration-300", className)}
      onError={() => setHasError(true)}
    />
  );
}
